import { c as _c } from "react/compiler-runtime";
import { graphql, useMutation, useQuery } from "@opensea/graphql";
import { TimelineSkeleton } from "@opensea/ui-kit";
import { isAfter, parseISO } from "date-fns";
import { ActionTimeline } from "../../../../../components/ActionTimeline";
import { ActionTimelineFragment } from "../../../../../components/ActionTimeline/ActionTimeline.fragment";
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../../../constants";
import { useAddress } from "../../../../../polychain/hooks/useAddress";
import { FlowActions } from "../../../../../stores/flowStore";
const query = graphql(`
    query CancelOrderActionTimelineQuery(
      $orderIds: [String!]!
      $address: Address!
      $gaslessCancelEligibleOrders: Boolean!
    ) {
      cancelOrders(
        orderIds: $orderIds
        address: $address
        gaslessCancelEligibleOrders: $gaslessCancelEligibleOrders
      ) {
        actions {
          __typename
          ...ActionTimeline
          ... on CancelOrdersAction {
            orders {
              id
            }
          }
          ... on GaslessCancelOrdersAction {
            orders {
              id
            }
          }
        }
      }
    }
  `, [ActionTimelineFragment]);
export function CancelOrderActionTimeline(t0) {
  const $ = _c(21);
  const {
    orderIds,
    gaslessCancelEligibleOrders,
    setView,
    setGaslessCancelSignatureMap
  } = t0;
  const address = useAddress();
  const t1 = address ?? "";
  let t2;
  if ($[0] !== gaslessCancelEligibleOrders || $[1] !== orderIds || $[2] !== t1) {
    t2 = {
      orderIds,
      address: t1,
      gaslessCancelEligibleOrders
    };
    $[0] = gaslessCancelEligibleOrders;
    $[1] = orderIds;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !address;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      query,
      variables: t2,
      pause: t3,
      context: NETWORK_ONLY_OPERATION_CONTEXT
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const [t5] = useQuery(t4);
  const {
    data,
    fetching
  } = t5;
  const [, gaslessCancelMutation] = useMutation(GaslessCancelOrderMutation);
  if (fetching) {
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <TimelineSkeleton count={1} />;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  if (!data) {
    throw new Error("There was a problem with cancelling your orders.");
  }
  const actions = data.cancelOrders.actions;
  let t6;
  if ($[8] !== actions) {
    t6 = actions.flatMap(_temp);
    $[8] = actions;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const cancelOrderAction = t6[0];
  let t7;
  if ($[10] !== actions) {
    t7 = actions.flatMap(_temp2);
    $[10] = actions;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const gaslessCancelOrderAction = t7[0];
  const hasGaslessCancelOrders = gaslessCancelEligibleOrders && !!gaslessCancelOrderAction;
  const orders = hasGaslessCancelOrders ? gaslessCancelOrderAction.orders : cancelOrderAction.orders;
  let t8;
  if ($[12] !== gaslessCancelMutation || $[13] !== hasGaslessCancelOrders || $[14] !== orders || $[15] !== setGaslessCancelSignatureMap || $[16] !== setView) {
    t8 = async lastResponse => {
      if (hasGaslessCancelOrders && lastResponse.action === "SIGNATURE") {
        const orderIds_0 = orders.map(_temp3);
        const {
          data: data_0,
          error
        } = await gaslessCancelMutation({
          orderIds: orderIds_0,
          signature: lastResponse.signature
        });
        if (error) {
          throw new Error("Something went wrong");
        }
        if (data_0) {
          const signatureExpirationMap = {};
          for (const {
            orderId,
            expiration
          } of data_0.gaslessCancelOrders.lastSignatureIssuedExpiration) {
            signatureExpirationMap[orderId] = expiration && isAfter(parseISO(expiration), new Date()) ? expiration : "";
          }
          setGaslessCancelSignatureMap(signatureExpirationMap);
          FlowActions.onSuccess();
        }
      }
      setView("done");
    };
    $[12] = gaslessCancelMutation;
    $[13] = hasGaslessCancelOrders;
    $[14] = orders;
    $[15] = setGaslessCancelSignatureMap;
    $[16] = setView;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== actions || $[19] !== t8) {
    t9 = <ActionTimeline actions={actions} onAllActionsCompleted={t8} />;
    $[18] = actions;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
function _temp3(o) {
  return o.id;
}
function _temp2(a_0) {
  return a_0.__typename === "GaslessCancelOrdersAction" ? [a_0] : [];
}
function _temp(a) {
  return a.__typename === "CancelOrdersAction" ? [a] : [];
}
const GaslessCancelOrderMutation = graphql(`
  mutation GaslessCancelOrderMutation(
    $orderIds: [String!]!
    $signature: String!
  ) {
    gaslessCancelOrders(orderIds: $orderIds, signature: $signature) {
      lastSignatureIssuedExpiration {
        orderId
        expiration
      }
    }
  }
`);